import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/lib/auth/store";
import { can, type Capability } from "@/lib/auth/rbac";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ShieldAlert } from "lucide-react";

interface Props {
  children: React.ReactNode;
  /** When set, only roles holding this capability can render. */
  require?: Capability;
}

export function ProtectedRoute({ children, require }: Props) {
  const location = useLocation();
  const session = useAuthStore((s) => s.session);
  const refresh = useAuthStore((s) => s.refreshToken);

  if (!session || !refresh) {
    return (
      <Navigate
        to={`/login?from=${encodeURIComponent(location.pathname)}`}
        replace
      />
    );
  }

  if (require && !can(session.role, require)) {
    return (
      <div className="mx-auto max-w-md p-8">
        <Card>
          <CardHeader>
            <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-destructive/10">
              <ShieldAlert className="size-7 text-destructive" />
            </div>
            <CardTitle className="text-center pt-2">Access denied</CardTitle>
            <CardDescription className="text-center">
              Your role ({session.role}) doesn't have permission to view
              this page. Contact a Super Admin if you need access.
            </CardDescription>
          </CardHeader>
          <CardContent />
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
